<template>
  <div
    class="flex-grow relative"
    :class="classWrapper"
    @mouseenter="modelIsMouseOver = true"
    @mouseleave="modelIsMouseOver = false"
    >
    <ui-text-label
      v-if="label && computedId"
      :for="computedId"
      :text="label"
      class="block leading-[0] ml-3 z-10 relative"
      :disabled="disabled"
      :required="required"
      />
    <textarea
      v-if="rows"
      :id="computedId ?? ''"
      ref="refTextarea"
      v-model="(model as any)"
      :class="[$attrs.class, $props.class]"
      :type="type ?? 'text'"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      v-bind="$attrs"
      data-component="UiText"
      :required="required"
      :readonly="readonly"
      :disabled="disabled"
      :rows="rows"
      @click="$emit('click')"
      @focus="dataIsFocused = true; dataIsVirgin = false;"
      @blur="dataIsFocused = false"
      />
    <input
      v-else
      :id="computedId ?? ''"
      ref="refInput"
      v-model="(model as string)"
      :class="[$attrs.class, $props.class]"
      :type="type ?? 'text'"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      v-bind="$attrs"
      data-component="UiText"
      :required="required"
      :readonly="readonly"
      :disabled="disabled"
      @click="$emit('click')"
      @focus="dataIsFocused = true; dataIsVirgin = false;"
      @blur="dataIsFocused = false"
      >
    <ui-validator
      v-model="modelIsValid"
      v-model:message="dataValidationMessage"
      :is-virgin="dataIsVirgin"
      :is-focused="dataIsFocused"
      :validation="validation ?? (rows ? () => { return true} : Validator.default)"
      :only-error="!validation"
      :value="(model as string)"
      :validation_delay="validation_delay"
      />
    <label
      v-if="(computedErrors && ((!dataIsVirgin && modelIsMouseOver) || dataIsFocused))"
      :for="computedId ?? ''"
      class="font-semibold text-sm absolute bg-white drop-shadow-xl p-2 z-50 left-0 right-0 whitespace-pre-line"
      :class="{
        'text-red-600': modelIsValid === false,
        'text-green-600': modelIsValid === true,
        'text-gray-600 animate-pulse': modelIsValid === null,
      }"
      v-text="modelIsValid === null ? 'Loading ...' : computedErrors"
      />
  </div>
</template>

<script setup lang="ts">
defineOptions({ inheritAttrs: false }) // prevent listeners from auto-binding to the root

const emit = defineEmits(['click','bounced'])

const props = defineProps<{
	id?: string
	label?: string
	type?: string
	error?: string
	autocomplete?: string
	placeholder?: string,
	required?: boolean
	readonly?: boolean
	rows?: any
  validation?: (newVal: any, oldVal: any, bagging: any) => any|Promise<any>,
  validation_delay?: number
  disabled?: boolean
  class?: string|object|any[]
  classWrapper?: string|object|any[]
}>()

const refTextarea = ref<HTMLElement>();
const refInput = ref<HTMLElement>();

const model = defineModel<string|null|number|undefined>()
const modelIsMouseOver = defineModel<boolean>('mouseover')
const modelFocus = defineModel<boolean>('focus')
const modelIsValid = defineModel<boolean|null>('is-valid')

const dataIsFocused = ref<boolean>(false)
const dataIsVirgin = ref<boolean>(true)
const dataAcknowledgedError = ref<string>('')
const dataValidationMessage = ref<string|undefined>('')
const dataId = ref<string|null|undefined>(props?.id ?? `input-${Math.random().toString(36).substring(7)}`)

onMounted(async () => {
  methodResize()
})

const computedId = computed(() => {
  return props.id ?? dataId.value
})

const computedErrors = computed(() => {
  if(dataValidationMessage.value) {
    // if dataValidationMessage is string[]
    if(Array.isArray(dataValidationMessage.value)) {
      return dataValidationMessage.value.join('\n')
    }

    return dataValidationMessage.value
  }

  if(
    props.error
		&& dataAcknowledgedError.value != props.error
  ) {
    return props.error
  }

  return ''
})

const methodResize = () => {
  nextTick(() => {
    if (refTextarea.value) {
      refTextarea.value.style.height = 'auto'

      if(refTextarea.value?.clientHeight) {
        refTextarea.value.style.height = (refTextarea.value.scrollHeight + 2) + 'px'
      }
    }
  })
}

const methodFocus = () => {
  if (refInput.value) {
    refInput.value?.focus()
  }
}

watch(() => model.value, () => {
  dataIsVirgin.value = false
  if (props.rows) methodResize()
})

watch(() => dataIsFocused.value, (v) => {
  modelFocus.value = v

  if(!v) {
    emit('bounced', model.value)
  }
})

watch(() => modelFocus, (v) => {
  if(v.value) {
    methodFocus()
  }else {
    if (refInput.value) {
      refInput.value?.blur()
    }
  }
})

defineExpose({
  focus: methodFocus,
})

onBeforeUnmount(() => {
  if(!dataIsVirgin.value){
    emit('bounced', model.value)
  }
})
</script>
